import { FeatureSet, ReleaseStage, stateUi, Theme, wantAtLeastReleaseStage } from '@infinitro/config';
import { Verbiage } from '@infinitro/content';
import { isNotificationsOpen, isPlayerChatOpen } from '@infinitro/global';
import { get } from 'svelte/store';
let playerChatOpen = false;
isPlayerChatOpen.subscribe((value) => (playerChatOpen = value));
const onShowPlayerChat = () => {
    isNotificationsOpen.set(false);
    isPlayerChatOpen.set(!playerChatOpen);
};
const stateUiValue = get(stateUi);
const categories = [
    {
        id: 'fomo-points',
        name: Verbiage.fomo_points,
        icon: 'PointsIcon',
        sidebarOrder: 1,
        href: '/tournaments/#/fomo-airdrop/home',
        themes: [Theme.Fomo]
    },
    {
        id: 'calendar',
        name: Verbiage.christmas_calendar,
        icon: 'GiftIcon',
        sidebarOrder: 1,
        href: '/calendar/',
        themes: [Theme.Flush],
        authed: true,
        startDate: '2025-12-01T00:00:00Z',
        endDate: '2025-12-31T23:59:59Z'
    },
    {
        id: 'promotions',
        name: Verbiage.promotions,
        icon: 'PromotionIcon',
        sidebarOrder: 2,
        href: '/promotion-page/'
    },
    {
        id: 'referral',
        name: Verbiage.referral,
        icon: stateUiValue?.layout?.useReferralIconAnimated ? 'ReferralIconAnimated' : 'ReferralIcon',
        sidebarOrder: 3,
        href: '/referral/',
        themes: [Theme.Flush, Theme.Fomo, Theme.Luna, Theme.Rocketbet]
    },
    {
        id: 'vip',
        name: Verbiage.vip,
        icon: 'VipIcon',
        iconSize: get(stateUi)?.sidebar?.sidebarStyle === 'alternative2' ? 24 : undefined,
        sidebarOrder: 4,
        href: '/vip-dashboard/'
    },
    {
        id: 'flush-races',
        name: Verbiage.races,
        icon: 'LeaderboardIcon',
        sidebarOrder: 5,
        href: '/promotion-page/race/flush-races/',
        themes: [Theme.Flush]
    },
    {
        id: 'blog',
        name: Verbiage.blog,
        icon: 'BlogIcon',
        sidebarOrder: 6,
        href: '/blog',
        external: true,
        themes: [Theme.Flush]
    },
    {
        id: 'transparency',
        name: Verbiage.transparency,
        icon: 'TransparencyIcon',
        sidebarOrder: 7,
        href: '/transparency/',
        themes: [Theme.Flush],
        rel: FeatureSet.Preview
    },
    {
        id: 'tournaments',
        name: Verbiage.tournaments,
        icon: 'TournamentsIcon',
        sidebarOrder: 8,
        href: '/tournaments/#/tournaments',
        themes: [Theme.Fomo, Theme.Luna, Theme.Rocketbet]
    },
    {
        id: 'raffles',
        name: Verbiage.raffles,
        icon: 'RafflesIcon',
        sidebarOrder: 9,
        href: '/tournaments/#/raffles',
        themes: [Theme.Fomo, Theme.Luna, Theme.Rocketbet]
    },
    {
        id: 'special-bets',
        name: Verbiage.FOMO_bets,
        icon: 'BetsIcon',
        sidebarOrder: 10,
        href: '/special-bets/',
        themes: [Theme.Fomo]
    },
    {
        id: 'special-bets-luna',
        name: Verbiage.luna_bets,
        icon: 'LunaBetsIcon',
        sidebarOrder: 10,
        href: '/special-bets/',
        themes: [Theme.Luna]
    },
    {
        id: 'special-bets-turbo',
        name: Verbiage.turbo_bets,
        icon: 'BetsIcon',
        sidebarOrder: 10,
        href: '/special-bets/',
        themes: [Theme.Turboslots]
    },
    {
        id: 'jackpot',
        name: Verbiage.FOMO_jackpot,
        icon: 'JackpotIcon',
        sidebarOrder: 11,
        href: '/promotion-page/jackpot/',
        themes: [Theme.Fomo]
    },
    {
        id: 'jackpot',
        name: Verbiage.jackpot,
        icon: 'JackpotIcon',
        sidebarOrder: 2,
        href: '/promotion-page/jackpot/',
        themes: [Theme.Luna, Theme.Turboslots]
    },
    {
        id: 'chat',
        name: Verbiage.chat,
        icon: 'ChatIcon',
        sidebarOrder: 12,
        href: '/#/',
        themes: [Theme.Fomo, Theme.Rocketbet],
        authed: true,
        action: onShowPlayerChat
    }
];
const toMs = (d) => {
    if (!d)
        return undefined;
    const dt = typeof d === 'string' ? new Date(d) : d;
    const ms = dt.getTime();
    return Number.isFinite(ms) ? ms : undefined;
};
const isWithinDateRange = (item, nowMs) => {
    const startMs = toMs(item.startDate);
    const endMs = toMs(item.endDate);
    if (startMs !== undefined && nowMs < startMs)
        return false;
    if (endMs !== undefined && nowMs > endMs)
        return false;
    return startMs !== undefined || endMs !== undefined;
};
const categorySortOrder = (item, theme, sidebarUi) => {
    if (sidebarUi?.vipBeforePromotions) {
        if (item.id === 'vip')
            return 2;
        if (item.id === 'promotions')
            return 3;
    }
    return item.sidebarOrderByTheme?.[theme] ?? item.sidebarOrder ?? 9999;
};
export const getCategories = (loggedIn, featureSet, theme, races, mobile, sidebarUi) => {
    const hasRaces = Array.isArray(races) && races.length > 0;
    const nowMs = Date.now();
    return categories
        .map((item) => {
        const hideForLogin = item.id === 'fomo-points' && loggedIn === false;
        const hideFlushRaces = item.id === 'flush-races' && !hasRaces;
        const hideChatOnDesktop = item.id === 'chat' && !mobile;
        const hasDateRule = item.startDate || item.endDate;
        const hideByDate = hasDateRule ? !isWithinDateRange(item, nowMs) : false;
        return {
            ...item,
            hide: item.hide || hideForLogin || hideFlushRaces || hideByDate || hideChatOnDesktop
        };
    })
        .filter((item) => {
        const brandRel = item.id === 'referral' ? get(stateUi).referral?.rel : undefined;
        const effectiveRel = item.rel ?? brandRel;
        return ((!effectiveRel || effectiveRel === featureSet) &&
            (!item.themes || item.themes.includes(theme)) &&
            !item.hide &&
            !(item.authed === true && !loggedIn) &&
            (!item.stage || wantAtLeastReleaseStage(item.stage)));
    })
        .filter((cat) => cat.sidebarOrder && cat.sidebarOrder > 0)
        .map((item) => {
        let name = item.name;
        if (item.id === 'vip' && sidebarUi?.vipNavRowLabelVariant === 'alternate_vip_heading') {
            name = Verbiage.vip_galaxy;
        }
        if (item.id === 'tournaments' &&
            sidebarUi?.tournamentsNavRowLabelVariant === 'alternate_tournaments_heading') {
            name = Verbiage.rocket_races;
        }
        let href = item.href;
        if (item.name === Verbiage.referral) {
            href = loggedIn ? '/referral-dashboard/' : '/referral/';
        }
        if (item.id === 'vip') {
            href = loggedIn ? '/vip-dashboard/' : '/vip/';
        }
        return { ...item, name, href };
    })
        .sort((a, b) => categorySortOrder(a, theme, sidebarUi) - categorySortOrder(b, theme, sidebarUi));
};
